/**
 * unspun shared Klaviyo Client API helper.
 * Mirrors the pattern proven in assets/pdp-signup.js (v3, 2026-05-11).
 *
 * Built 2026-05-12 evening to fix the Quiz/Calc profile-property gap
 * (legacy _learnq.push + manage.kmail-lists subscribe pattern was fire-and-forget,
 * left ~33% of profiles on the list with no properties).
 *
 * Exposes window.unspunKlaviyo.submitWithProperties(email, properties, listId, source).
 *
 * Returns a Promise that resolves on success, rejects with Error on failure.
 * Caller MUST await and handle errors with a user-facing message.
 *
 * - POST /client/profiles/  → creates/updates profile with arbitrary properties
 * - POST /client/subscriptions/  → adds to list (this endpoint inherently records
 *   marketing consent — do NOT put `marketing_consent` on profile attributes, returns 400)
 */
(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'U3dkRr';
  var KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_REVISION = '2024-10-15';

  function readResponse(res, label) {
    return res.text().then(function (text) {
      var parsed = null;
      try { parsed = JSON.parse(text); } catch (e) {}
      if (window.__unspunLog) {
        window.__unspunLog('klaviyo-client: ' + label, {
          status: res.status, ok: res.ok, body: parsed || text.slice(0, 500)
        });
      }
      return { res: res, body: parsed, text: text };
    });
  }

  /**
   * Submit profile + subscription to Klaviyo via Client API.
   * @param {string} email - profile email (required)
   * @param {object} properties - profile properties (do NOT include $email or marketing_consent)
   * @param {string} listId - Klaviyo list ID to subscribe to
   * @param {string} source - signup_source value (also passed as custom_source to subscriptions)
   * @returns {Promise<{ res, body, text }>} resolves on success, rejects with Error on any non-2xx
   */
  function submitWithProperties(email, properties, listId, source) {
    if (!email) return Promise.reject(new Error('email required'));
    if (!listId) return Promise.reject(new Error('listId required'));
    if (!source) return Promise.reject(new Error('source required'));

    var props = Object.assign({}, properties || {});
    props.signup_source = source;

    var profilePayload = {
      data: {
        type: 'profile',
        attributes: { email: email, properties: props }
      }
    };

    return fetch(KLAVIYO_PROFILES_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify(profilePayload)
    })
      .then(function (r) { return readResponse(r, 'profile POST'); })
      .then(function (profileResult) {
        // Klaviyo treats 202 (Accepted) and 200 OK as success for profile create/update.
        // Non-2xx on profile is rare but we still proceed to subscription — properties
        // can be retried via the same call later. The list-subscribe is the critical step.
        if (!profileResult.res.ok && profileResult.res.status !== 202 && profileResult.res.status !== 409) {
          console.warn('[klaviyo-client] profile POST non-2xx (continuing to subscribe):', profileResult.res.status);
        }
        var subPayload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        };
        return fetch(KLAVIYO_SUBSCRIBE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
          body: JSON.stringify(subPayload)
        });
      })
      .then(function (r) { return readResponse(r, 'subscribe POST'); })
      .then(function (subResult) {
        if (!subResult.res.ok && subResult.res.status !== 202) {
          var detail = subResult.body && subResult.body.errors
            ? JSON.stringify(subResult.body.errors)
            : (subResult.text || '').slice(0, 200);
          throw new Error('Subscribe failed: ' + subResult.res.status + ' ' + detail);
        }
        return subResult;
      });
  }

  window.unspunKlaviyo = { submitWithProperties: submitWithProperties };
})();
